/* ============================================================
   THE ARCHER — Global theme behaviour
   - Lenis smooth scroll bridged to GSAP ScrollTrigger
   - Custom cursor (fine-pointer only)
   - Preloader
   - Side-dots scroll spy
   - Reveal-on-scroll observer (.ta-reveal)
   - Live AJAX cart drawer + quick-add + toast
   Dependencies (loaded from CDN in theme.liquid): gsap,
   ScrollTrigger, Lenis. All are used defensively (feature
   detected) so the theme degrades gracefully without them.
   ============================================================ */
(function () {
  "use strict";

  const prefersReduced = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  const coarse = window.matchMedia("(pointer: coarse)").matches;

  /* -------------------------------------------------- *
   * 1. Smooth scroll (Lenis) bridged into GSAP
   * -------------------------------------------------- */
  let lenis = null;
  function initSmoothScroll() {
    if (prefersReduced || typeof window.Lenis === "undefined") return;
    lenis = new window.Lenis({
      duration: 1.7,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      smoothWheel: true,
      wheelMultiplier: 0.75,
    });

    if (window.gsap && window.ScrollTrigger) {
      lenis.on("scroll", window.ScrollTrigger.update);
      window.gsap.ticker.add((time) => lenis.raf(time * 1000));
      window.gsap.ticker.lagSmoothing(0);
      window.addEventListener("load", () => window.ScrollTrigger.refresh());
    } else {
      const raf = (time) => {
        lenis.raf(time);
        requestAnimationFrame(raf);
      };
      requestAnimationFrame(raf);
    }

    // Anchor links -> smooth scroll through Lenis
    document.addEventListener("click", (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (id.length < 2) return;
      const target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -80 });
    });
  }
  window.TheArcher = window.TheArcher || {};
  window.TheArcher.lenis = () => lenis;

  /* -------------------------------------------------- *
   * 2. Custom cursor
   * -------------------------------------------------- */
  function initCursor() {
    if (coarse) return;
    const dot = document.querySelector(".ta-cursor");
    const ring = document.querySelector(".ta-cursor-ring");
    const pill = document.querySelector(".ta-cursor-pill");
    if (!dot || !ring || !pill) return;

    document.body.classList.add("ta-cursor-on");

    let mx = -100,
      my = -100; // mouse
    let rx = -100,
      ry = -100; // dot (fast spring)
    let tx = -100,
      ty = -100; // ring (slow trail)

    window.addEventListener("mousemove", (e) => {
      mx = e.clientX;
      my = e.clientY;

      const labelEl = e.target.closest("[data-cursor-text]");
      const el = labelEl || e.target.closest("[data-cursor]");
      const variant = el ? el.getAttribute("data-cursor") || "link" : "default";

      dot.classList.toggle("is-link", variant === "link" && !labelEl);
      dot.classList.toggle("is-drag", variant === "drag" && !labelEl);
      dot.classList.toggle("is-hidden", !!labelEl);
      ring.style.opacity = labelEl ? "0" : "0.7";

      dot.querySelector(".ta-cursor__drag-label")?.style.setProperty(
        "display",
        variant === "drag" && !labelEl ? "block" : "none"
      );

      if (labelEl) {
        pill.textContent = labelEl.getAttribute("data-cursor-text");
        pill.classList.add("is-on");
      } else {
        pill.classList.remove("is-on");
      }
    });

    window.addEventListener("click", (e) => {
      const r = document.createElement("div");
      r.className = "ta-cursor-ripple";
      r.style.left = e.clientX + "px";
      r.style.top = e.clientY + "px";
      document.body.appendChild(r);
      setTimeout(() => r.remove(), 650);
    });

    function raf() {
      rx += (mx - rx) * 0.35;
      ry += (my - ry) * 0.35;
      tx += (mx - tx) * 0.15;
      ty += (my - ty) * 0.15;
      dot.style.transform = `translate(${rx}px, ${ry}px)`;
      pill.style.transform = `translate(${rx}px, ${ry}px)`;
      ring.style.transform = `translate(${tx}px, ${ty}px)`;
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
  }

  /* -------------------------------------------------- *
   * 3. Preloader
   * -------------------------------------------------- */
  function initPreloader() {
    const pre = document.querySelector(".ta-preloader");
    if (!pre) return;
    if (prefersReduced) {
      pre.remove();
      return;
    }
    const fill = pre.querySelector(".ta-preloader__fill");
    const pctEl = pre.querySelector(".ta-preloader__pct");
    const start = Date.now();
    const duration = 1400;
    (function tick() {
      const p = Math.min(100, Math.round(((Date.now() - start) / duration) * 100));
      if (fill) fill.style.width = p + "%";
      if (pctEl) pctEl.textContent = p + "%";
      if (p < 100) requestAnimationFrame(tick);
      else
        setTimeout(() => {
          pre.classList.add("is-done");
          setTimeout(() => pre.remove(), 850);
        }, 250);
    })();
  }

  /* -------------------------------------------------- *
   * 4. Side-dots scroll spy
   * -------------------------------------------------- */
  function initSideDots() {
    const dots = document.querySelectorAll(".ta-side-dots__link");
    if (!dots.length) return;
    const map = {};
    dots.forEach((d) => {
      const id = d.getAttribute("data-target");
      const el = id && document.getElementById(id);
      if (el) map[id] = d;
    });
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            dots.forEach((d) => d.classList.remove("is-active"));
            map[e.target.id]?.classList.add("is-active");
          }
        }),
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    Object.keys(map).forEach((id) =>
      observer.observe(document.getElementById(id))
    );
  }

  /* -------------------------------------------------- *
   * 5. Reveal-on-scroll observer
   * -------------------------------------------------- */
  function initReveal() {
    const els = document.querySelectorAll(".ta-reveal:not(.is-in)");
    if (!els.length) return;
    if (prefersReduced) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            obs.unobserve(e.target);
          }
        }),
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
  }
  window.TheArcher.initReveal = initReveal;

  /* -------------------------------------------------- *
   * 6. Cart (AJAX) + drawer + toast
   * -------------------------------------------------- */
  const money = (cents) => {
    const amount = (cents / 100).toFixed(2);
    const fmt = window.TheArcher.moneyFormat || "${{amount}}";
    return fmt.replace(/\{\{\s*amount(_no_decimals|_with_comma_separator)?\s*\}\}/, amount);
  };

  function toast(msg) {
    const t = document.querySelector(".ta-toast");
    if (!t) return;
    t.textContent = msg;
    t.classList.add("is-on");
    clearTimeout(t._timer);
    t._timer = setTimeout(() => t.classList.remove("is-on"), 2200);
  }
  window.TheArcher.toast = toast;

  const Cart = {
    open() {
      const d = document.querySelector("[data-cart-drawer]");
      if (!d) return;
      d.classList.add("is-open");
      d.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
    },
    close() {
      const d = document.querySelector("[data-cart-drawer]");
      if (!d) return;
      d.classList.remove("is-open");
      d.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
    },
    async add(variantId, quantity, name) {
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: quantity || 1 }],
          }),
        });
        if (!res.ok) throw new Error("add failed");
        await Cart.refresh();
        toast((name || "Item") + " added to bag");
        Cart.open();
        return true;
      } catch (err) {
        toast("Could not add to bag");
        return false;
      }
    },
    async changeLine(line, quantity) {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      });
      if (res.ok) Cart.render(await res.json());
    },
    async refresh() {
      const res = await fetch("/cart.js");
      if (res.ok) Cart.render(await res.json());
    },
    render(cart) {
      const drawer = document.querySelector("[data-cart-drawer]");
      if (!drawer) return;
      const body = drawer.querySelector("[data-cart-body]");
      const subtotal = drawer.querySelector("[data-cart-subtotal]");
      const counts = document.querySelectorAll("[data-cart-count]");

      counts.forEach((c) => {
        c.textContent = cart.item_count;
        c.classList.toggle("is-empty", cart.item_count === 0);
      });

      if (subtotal) subtotal.textContent = money(cart.total_price);

      if (!body) return;
      if (!cart.items.length) {
        body.innerHTML =
          '<p class="ta-cart__empty">Your bag is empty — go find your perfect match.</p>';
        return;
      }
      body.innerHTML = cart.items
        .map(
          (it, i) => `
        <div class="ta-cart__item" data-line="${i + 1}">
          <a href="${it.url}" class="ta-cart__item-img">
            <img src="${
              it.image ? it.image.replace(/(\.[^.]*)$/, "_120x$1") : ""
            }" alt="${it.product_title}" loading="lazy" width="64" height="64">
          </a>
          <div class="ta-cart__item-info">
            <h5 class="ta-cart__item-name">${it.product_title}</h5>
            ${
              it.variant_title && it.variant_title !== "Default Title"
                ? `<span class="ta-cart__item-variant">${it.variant_title}</span>`
                : ""
            }
            <span class="ta-cart__item-price">${money(it.final_price)}</span>
            <div class="ta-cart__qty">
              <button type="button" data-qty-down aria-label="Decrease quantity">−</button>
              <span>${it.quantity}</span>
              <button type="button" data-qty-up aria-label="Increase quantity">+</button>
            </div>
          </div>
        </div>`
        )
        .join("");
    },
  };
  window.TheArcher.cart = Cart;

  function bindCart() {
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-cart-open]")) {
        e.preventDefault();
        Cart.open();
      }
      if (
        e.target.closest("[data-cart-close]") ||
        e.target.closest("[data-cart-overlay]")
      ) {
        Cart.close();
      }
      const qa = e.target.closest("[data-quick-add]");
      if (qa) {
        e.preventDefault();
        const id = qa.getAttribute("data-variant-id");
        if (!id) {
          toast("Select options on the product page");
          return;
        }
        qa.classList.add("is-loading");
        Cart.add(id, 1, qa.getAttribute("data-product-title")).then(() => {
          qa.classList.remove("is-loading");
          qa.classList.add("is-added");
          setTimeout(() => qa.classList.remove("is-added"), 1200);
        });
      }
      const up = e.target.closest("[data-qty-up]");
      const down = e.target.closest("[data-qty-down]");
      if (up || down) {
        const item = (up || down).closest("[data-line]");
        const line = item.getAttribute("data-line");
        const current = parseInt(
          item.querySelector(".ta-cart__qty span").textContent,
          10
        );
        Cart.changeLine(line, current + (up ? 1 : -1));
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") Cart.close();
    });

    // Keep drawer in sync on load
    Cart.refresh();
  }

  /* -------------------------------------------------- *
   * Boot
   * -------------------------------------------------- */
  function boot() {
    initSmoothScroll();
    initCursor();
    initPreloader();
    initSideDots();
    initReveal();
    bindCart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Re-run reveal observer when sections load in the theme editor
  document.addEventListener("shopify:section:load", () => {
    initReveal();
    initSideDots();
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
})();
